import React, { useCallback, useMemo, useState } from 'react';
import { useQuerySegments } from '../segments/segmentActions';
import {
  InternalApiEntityGroup,
  InternalApiEntityGroupScreeningConfiguration,
  InternalApiEntityType,
} from '../../../api-internal';
import { createColumnsForObjectData, SalvButton, SalvTable, Size } from '@salvapp/ui-components';
import {
  useQuerySegmentScreeningConfiguration,
  useUpdateSegmentScreeningConfiguration,
} from './segmentScreeningConfigurationActions';
import { partition } from 'lodash';
import './DailyScreeningConfigurationTable.scss';
import { showConfirmationModal } from '../../../confirmation-modal/confirmationModalReducer';
import { useDispatch } from 'react-redux';
import SalvPage from '../../../common/SalvPage';
import InternalLink from '../../../common/InternalLink';
import { useMe } from '../../../organisation/useMe';
import { CAN_MODIFY_ENTITY_GROUP_SCREENING_CONFIGURATION } from '../../../auth/permissions';
import { getConfigurationUrl } from '../../../navigation/helper';
import { ConfigurationProduct } from '../../../settings/configuration/Configuration';

const SegmentsLink = () => <InternalLink title={'Person Segments'} link={'/segments'} />;

const DailyScreeningConfiguration = () => {
  const dispatch = useDispatch();
  const { permissions: userPermissions } = useMe();
  const { data: segments } = useQuerySegments({ entityType: InternalApiEntityType.PERSON });
  const { data: segmentScreeningConfiguration } = useQuerySegmentScreeningConfiguration();
  const { mutate: updateSegmentScreeningConfiguration } = useUpdateSegmentScreeningConfiguration();

  const [selectedAvailableSegments, setSelectedAvailableSegments] = useState<InternalApiEntityGroup[]>([]);
  const [selectedExcludedSegments, setSelectedExcludedSegments] = useState<InternalApiEntityGroup[]>([]);

  const [excludedSegments, availableSegments] = useMemo(() => {
    const excludedSegmentCodes =
      segmentScreeningConfiguration
        ?.filter((config) => config.excludeFromDailyScreening)
        ?.map((config) => config.groupCode) ?? [];
    return partition(segments, (group) => excludedSegmentCodes.includes(group.code!));
  }, [segments, segmentScreeningConfiguration]);

  const clearSelections = useCallback(() => {
    setSelectedAvailableSegments([]);
    setSelectedExcludedSegments([]);
  }, []);

  const addToExcluded = useCallback(
    (groups: InternalApiEntityGroup[]) => {
      const configurations: InternalApiEntityGroupScreeningConfiguration[] = [...groups, ...excludedSegments].map(
        (group) => {
          return { groupCode: group.code!, excludeFromDailyScreening: true };
        }
      );
      updateSegmentScreeningConfiguration(configurations);
      clearSelections();
    },
    [excludedSegments, updateSegmentScreeningConfiguration, clearSelections]
  );

  const removeFromExcluded = useCallback(
    (groups: InternalApiEntityGroup[]) => {
      const configurations = excludedSegments
        .filter((group) => !groups.includes(group))
        .map((group) => {
          return { groupCode: group.code!, excludeFromDailyScreening: true };
        });
      updateSegmentScreeningConfiguration(configurations);
      clearSelections();
    },
    [excludedSegments, updateSegmentScreeningConfiguration, clearSelections]
  );

  const withConfirmation = useCallback((onConfirm: () => void) => {
    dispatch(
      showConfirmationModal({
        title: 'Do you want to change Automatic Re-screening configuration?',
        confirmationText: 'Yes',
        cancelText: 'No',
        onConfirm: onConfirm,
      })
    );
  }, []);

  const editingEnabled = userPermissions?.includes(CAN_MODIFY_ENTITY_GROUP_SCREENING_CONFIGURATION);

  return (
    <SalvPage
      title={'Automatic Re-screening'}
      breadcrumb={{
        title: 'Configuration',
        to: getConfigurationUrl(ConfigurationProduct.SCREENING),
      }}
      className={'daily-screening-configuration'}
    >
      <div className='text-s m-b-16' style={{ color: 'var(--text-medium)' }}>
        {'Select the '}
        {<SegmentsLink />}
        {
          ' you would like to exclude from being re-screened every 24 hours. All other Persons, regardless of their Segment, will be automatically re-screened daily if not listed in the Excluded table'
        }
      </div>
      <div className='table-container'>
        <SalvTable
          className='daily-screening-table'
          title='Available Segments'
          actions={
            selectedAvailableSegments.length > 0 && (
              <div className='buttons'>
                <SalvButton
                  className='m-r-8'
                  label='Dismiss'
                  onClick={() => setSelectedAvailableSegments([])}
                  variant='muted'
                  size={Size.SMALL}
                />
                <SalvButton
                  label='Move to Excluded'
                  onClick={() => withConfirmation(() => addToExcluded(selectedAvailableSegments))}
                  size={Size.SMALL}
                />
              </div>
            )
          }
          columns={createColumnsForObjectData<InternalApiEntityGroup>([
            {
              accessorKey: 'name',
              header: 'Segment Name',
            },
          ])}
          selectedRows={selectedAvailableSegments}
          setSelectedRows={setSelectedAvailableSegments}
          data={availableSegments}
          emptyMessage={'No available Segments'}
          isRowSelectionEnabled={() => editingEnabled}
          allowSelectAll={editingEnabled}
        />
        <SalvTable
          className='daily-screening-table'
          title='Excluded from Automatic Re-screening'
          actions={
            selectedExcludedSegments.length > 0 && (
              <div className='buttons'>
                <SalvButton
                  className='m-r-8'
                  label='Dismiss'
                  onClick={() => setSelectedExcludedSegments([])}
                  variant='muted'
                  size={Size.SMALL}
                />
                <SalvButton
                  label='Remove from Excluded'
                  onClick={() => withConfirmation(() => removeFromExcluded(selectedExcludedSegments))}
                  size={Size.SMALL}
                />
              </div>
            )
          }
          columns={createColumnsForObjectData<InternalApiEntityGroup>([
            {
              accessorKey: 'name',
              header: 'Segment Name',
            },
          ])}
          selectedRows={selectedExcludedSegments}
          setSelectedRows={setSelectedExcludedSegments}
          data={excludedSegments}
          emptyMessage={'No Segments have been excluded from Automatic Re-screening'}
          isRowSelectionEnabled={() => editingEnabled}
          allowSelectAll={editingEnabled}
        />
      </div>
    </SalvPage>
  );
};

export default DailyScreeningConfiguration;
